import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Checkbox } from "@libs/zapi-ui/ds/components/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@libs/zapi-ui/ds/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldTitle
} from "@libs/zapi-ui/ds/components/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText
} from "@libs/zapi-ui/ds/components/input-group";
import { RadioGroup, RadioGroupItem } from "@libs/zapi-ui/ds/components/radio-group";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { CaretDownIcon, CheckIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import type { TExportProjectOptions } from "../lib/pdf-export";

type TDocType = TExportProjectOptions["docType"];
type TReportType = TExportProjectOptions["reportType"];

type TExportOptionsModalProps = {
  open: boolean;
  onClose: () => void;
  onExport: (options: TExportProjectOptions) => void;
  isExporting: boolean;
  projectName: string;
};

function sanitizeFileName(value: string) {
  return value.replace(/[^\p{L}\p{N}_\-\s]/gu, "");
}

export function ExportOptionsModal({ open, onClose, onExport, isExporting, projectName }: TExportOptionsModalProps) {
  const { t } = useTranslation(["projectHome", "common"]);
  const { isLabs } = useMeStore();
  const { selectedProject } = useProjectStore();
  const isTemplateLess = selectedProject?.projectTemplateSlug === "";
  const [fileName, setFileName] = useState("");
  const [docType, setDocType] = useState<TDocType>("pdf");
  const [reportType, setReportType] = useState<TReportType>("full");
  const [includeDecisionFrame, setIncludeDecisionFrame] = useState(false);
  const [includeProjectInstructions, setIncludeProjectInstructions] = useState(false);
  const [includeAnalysisAndIntelligence, setIncludeAnalysisAndIntelligence] = useState(false);
  const [includeVault, setIncludeVault] = useState(false);
  const fileNameId = useId();
  const typeId = useId();

  useEffect(() => {
    if (open) {
      setFileName(sanitizeFileName(projectName).trim());
      setDocType("pdf");
      setReportType("full");
      setIncludeDecisionFrame(false);
      setIncludeProjectInstructions(false);
      setIncludeAnalysisAndIntelligence(false);
      setIncludeVault(false);
    }
  }, [open, projectName]);

  const docTypes: TDocType[] = isLabs ? ["pdf", "docx", "md"] : ["pdf", "docx"];

  const reportTypes: { value: TReportType; title: string; description: string; testId: string }[] = [
    {
      value: "full",
      title: t("exportOptions.fullReport"),
      description: isTemplateLess ? t("exportOptions.fullReportPages") : t("exportOptions.fullReportChapters"),
      testId: "export-type-full"
    },
    {
      value: "executive",
      title: t("exportOptions.summary"),
      description: t("exportOptions.summaryDescription"),
      testId: "export-type-executive"
    }
  ];

  const includes = [
    {
      id: "include-decision-frame",
      label: t("exportOptions.projectFrame"),
      checked: includeDecisionFrame,
      onChange: setIncludeDecisionFrame,
      shown: true
    },
    {
      id: "include-project-instructions",
      label: t("exportOptions.projectInstructions"),
      checked: includeProjectInstructions,
      onChange: setIncludeProjectInstructions,
      shown: isLabs
    },
    {
      id: "include-analysis-intelligence",
      label: t("exportOptions.analysisQuestions"),
      checked: includeAnalysisAndIntelligence,
      onChange: setIncludeAnalysisAndIntelligence,
      shown: !isTemplateLess
    },
    {
      id: "include-vault",
      label: t("exportOptions.vaultFilesAndLinks"),
      checked: includeVault,
      onChange: setIncludeVault,
      shown: true
    }
  ];

  const handleExport = () => {
    onExport({
      fileName: fileName.trim() || "report",
      docType: isTemplateLess ? "pdf" : docType,
      reportType,
      includeDecisionFrame,
      includeProjectInstructions,
      includeAnalysisAndIntelligence: isTemplateLess ? false : includeAnalysisAndIntelligence,
      includeVault
    });
  };

  const handleClose = () => {
    if (!isExporting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent closeLabel={t("exportOptions.close")} className="gap-5 rounded-2xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="typo-h5">{t("common:export")}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (isExporting || !fileName.trim()) return;
            handleExport();
          }}
        >
          <Field>
            <FieldLabel htmlFor={fileNameId}>{t("common:fileName")}</FieldLabel>
            <InputGroup data-disabled={isExporting || undefined}>
              <InputGroupInput
                id={fileNameId}
                value={fileName}
                onChange={(e) => setFileName(sanitizeFileName(e.target.value))}
                placeholder="report"
                disabled={isExporting}
                data-testid="export-filename-input"
              />
              <InputGroupAddon align="inline-end">
                {isTemplateLess ? (
                  <InputGroupText data-testid="export-format-pdf">.pdf</InputGroupText>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      data-testid="export-format-trigger"
                      disabled={isExporting}
                      render={<InputGroupButton />}
                    >
                      .{docType}
                      <CaretDownIcon />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" sideOffset={8} className="w-auto min-w-48 rounded-xl p-1.5">
                      {docTypes.map((type) => (
                        <DropdownMenuItem
                          key={type}
                          data-testid={`export-format-${type}`}
                          onClick={() => setDocType(type)}
                        >
                          <CheckIcon className={cn(docType === type ? "opacity-100" : "opacity-0")} />.{type}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </InputGroupAddon>
            </InputGroup>
          </Field>
          <FieldSet className="gap-3">
            <FieldLegend variant="label" className="mb-0">
              {t("exportOptions.type")}
            </FieldLegend>
            <RadioGroup
              value={reportType}
              disabled={isExporting}
              onValueChange={(next) => setReportType(next as TReportType)}
              className="gap-2"
            >
              {reportTypes.map((option) => {
                const id = `${typeId}-${option.value}`;
                return (
                  <FieldLabel key={option.value} htmlFor={id} className="rounded-xl" data-testid={option.testId}>
                    <Field orientation="horizontal" className="rounded-xl">
                      <FieldContent>
                        <FieldTitle>{option.title}</FieldTitle>
                        <FieldDescription>{option.description}</FieldDescription>
                      </FieldContent>
                      <RadioGroupItem value={option.value} id={id} />
                    </Field>
                  </FieldLabel>
                );
              })}
            </RadioGroup>
          </FieldSet>
          <FieldSet className="gap-3">
            <FieldLegend variant="label" className="mb-0">
              {t("exportOptions.include")}
            </FieldLegend>
            <FieldGroup className="gap-3">
              {includes
                .filter((include) => include.shown)
                .map((include) => (
                  <Field key={include.id} orientation="horizontal" data-disabled={isExporting || undefined}>
                    <Checkbox
                      id={include.id}
                      checked={include.checked}
                      onCheckedChange={(checked) => include.onChange(checked === true)}
                      disabled={isExporting}
                      data-testid={include.id}
                    />
                    <FieldLabel htmlFor={include.id} className="font-normal">
                      {include.label}
                    </FieldLabel>
                  </Field>
                ))}
            </FieldGroup>
          </FieldSet>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="ghost"
              shape="pill"
              data-testid="export-options-cancel-button"
              onClick={handleClose}
              disabled={isExporting}
            >
              {t("common:cancel")}
            </Button>
            <Button type="submit" shape="pill" disabled={isExporting || !fileName.trim()} data-testid="export-submit">
              {isExporting && <Spinner data-icon="inline-start" aria-label={t("common:loading")} />}
              {isExporting ? t("common:exporting") : t("common:export")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
